<template>
  <Teleport to="body">
    <div v-if="visible" class="power-order-dialog">
    <div class="dialog-overlay" @click="close"></div>
    <div class="dialog-content">
      <div class="container">
        <div class="header">
          <h1><i class="fas fa-receipt"></i> 我的订单</h1>
          <p class="subtitle">查看您的算力购买记录</p>
        </div>

        <!-- 订单列表 -->
        <div class="order-table-wrapper" v-loading="loading" :element-loading-text="$t('manga.order.loading')"
          element-loading-background="rgba(15, 23, 42, 0.8)">
          <table class="order-table" v-if="orderList.length > 0">
            <thead>
              <tr>
                <th>订单号</th>
                <th>积分数</th>
                <th>金额</th>
                <th>支付类型</th>
                <th>支付状态</th>
                <th>支付时间</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="order in orderList" :key="order.id">
                <td class="order-no">{{ order.orderNo }}</td>
                <td class="points">
                  <span class="points-value">{{ order.pointsAmount }}</span>
                </td>
                <td class="amount">
                  <span v-if="order.orderAmount != null">¥{{ order.orderAmount }}</span>
                  <span v-else class="text-muted">-</span>
                </td>
                <td>
                  <span class="type-tag" :class="getTypeClass(order.type)">{{ getTypeText(order.type) }}</span>
                </td>
                <td>
                  <span class="state-tag" :class="getStateClass(order.state)">{{ getStateText(order.state) }}</span>
                </td>
                <td class="time">{{ formatTime(order.time) }}</td>
              </tr>
            </tbody>
          </table>

          <!-- 空状态 -->
          <div class="empty-state" v-if="!loading && orderList.length === 0">
            <i class="fas fa-inbox"></i>
            <p>暂无订单记录</p>
          </div>
        </div>

        <!-- 分页 -->
        <div class="pagination-wrapper" v-if="pagination.total > 0">
          <el-pagination v-model:current-page="pagination.pageNo" v-model:page-size="pagination.pageSize"
            :page-sizes="[10, 20, 50]" :total="pagination.total"
            layout="total, sizes, prev, pager, next" @size-change="handleSizeChange"
            @current-change="handleCurrentChange" />
        </div>
      </div>

      <button class="close-button" @click="close">
        <i class="fas fa-times"></i>
      </button>
    </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { getUserPowerOrderList } from '@/api/manga/manga.js'

const { t } = useI18n()

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:visible'])

const loading = ref(false)
const orderList = ref([])
const pagination = ref({
  pageNo: 1,
  pageSize: 10,
  total: 0
})

// 监听弹窗打开，自动加载数据
watch(() => props.visible, (val) => {
  if (val) {
    pagination.value.pageNo = 1
    fetchOrderList()
  }
})

const fetchOrderList = async () => {
  loading.value = true
  try {
    const res = await getUserPowerOrderList({
      pageNo: pagination.value.pageNo,
      pageSize: pagination.value.pageSize
    })
    if (res.code === 200 && res.data) {
      orderList.value = res.data.records || []
      pagination.value.total = res.data.total || 0
    }
  } catch (error) {
    console.error('获取订单列表失败：', error)
  } finally {
    loading.value = false
  }
}

const handleSizeChange = (val) => {
  pagination.value.pageSize = val
  pagination.value.pageNo = 1
  fetchOrderList()
}

const handleCurrentChange = (val) => {
  pagination.value.pageNo = val
  fetchOrderList()
}

// 支付类型映射
const typeClassMap = { 1: 'type-gift', 2: 'type-alipay', 3: 'type-wechat', 4: 'type-bilibili', 5: 'type-paypal' }
const getTypeText = (type) => {
  const map = { 1: t('manga.order.typeGift'), 2: t('manga.order.typeAlipay'), 3: t('manga.order.typeWechat'), 4: t('manga.order.typeBilibili'), 5: t('manga.order.typePaypal') }
  return map[type] || t('manga.order.unknown')
}
const getTypeClass = (type) => typeClassMap[type] || ''

// 支付状态映射
const stateClassMap = { 1: 'state-pending', 2: 'state-success', 3: 'state-failed', 4: 'state-confirming', 5: 'state-closed' }
const getStateText = (state) => {
  const map = { 1: t('manga.order.statePending'), 2: t('manga.order.stateSuccess'), 3: t('manga.order.stateFailed'), 4: t('manga.order.stateConfirming'), 5: t('manga.order.stateClosed') }
  return map[state] || t('manga.order.unknown')
}
const getStateClass = (state) => stateClassMap[state] || ''

// 格式化时间
const formatTime = (time) => {
  if (!time) return '-'
  const d = new Date(time)
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const close = () => {
  emit('update:visible', false)
}
</script>

<style scoped>
.power-order-dialog {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.dialog-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(6px);
}

.dialog-content {
  position: relative;
  max-width: 900px;
  max-height: 85vh;
  width: 100%;
  background: rgba(15, 23, 42, 0.95);
  border: 1px solid rgba(94, 234, 212, 0.2);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(20px);
}

.close-button {
  position: absolute;
  top: 16px;
  right: 16px;
  background: rgba(94, 234, 212, 0.1);
  border: 1px solid rgba(94, 234, 212, 0.2);
  color: #5eead4;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  z-index: 10;
}

.close-button:hover {
  background: rgba(94, 234, 212, 0.2);
  transform: scale(1.05);
  border-color: rgba(94, 234, 212, 0.4);
}

.container {
  padding: 30px 24px;
  max-height: 85vh;
  overflow-y: auto;
}

/* 自定义滚动条 */
.container::-webkit-scrollbar {
  width: 6px;
}

.container::-webkit-scrollbar-track {
  background: rgba(30, 41, 59, 0.3);
  border-radius: 3px;
}

.container::-webkit-scrollbar-thumb {
  background: rgba(94, 234, 212, 0.4);
  border-radius: 3px;
}

.container::-webkit-scrollbar-thumb:hover {
  background: rgba(94, 234, 212, 0.6);
}

.container {
  scrollbar-width: thin;
  scrollbar-color: rgba(94, 234, 212, 0.4) rgba(30, 41, 59, 0.3);
}

.header {
  text-align: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(94, 234, 212, 0.1);
}

.header h1 {
  color: #5eead4;
  font-size: 1.6rem;
  margin-bottom: 6px;
  font-weight: 700;
}

.header .subtitle {
  color: #94a3b8;
  font-size: 0.9rem;
}

/* 表格 */
.order-table-wrapper {
  min-height: 200px;
}

.order-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}

.order-table th {
  text-align: left;
  padding: 12px 10px;
  color: #94a3b8;
  font-weight: 600;
  font-size: 0.8rem;
  border-bottom: 1px solid rgba(94, 234, 212, 0.15);
  white-space: nowrap;
}

.order-table td {
  padding: 12px 10px;
  color: #e2e8f0;
  border-bottom: 1px solid rgba(51, 65, 85, 0.4);
}

.order-table tbody tr:hover {
  background: rgba(51, 65, 85, 0.3);
}

.order-no {
  font-family: monospace;
  color: #94a3b8;
  font-size: 0.8rem;
}

.points-value {
  color: #fbbf24;
  font-weight: 600;
}

.amount {
  color: #5eead4;
  font-weight: 600;
}

.text-muted {
  color: #64748b;
}

.time {
  color: #94a3b8;
  font-size: 0.8rem;
  white-space: nowrap;
}

/* 类型标签 */
.type-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
}

.type-gift {
  color: #fbbf24;
  background: rgba(251, 191, 36, 0.15);
}

.type-alipay {
  color: #60a5fa;
  background: rgba(96, 165, 250, 0.15);
}

.type-wechat {
  color: #4ade80;
  background: rgba(74, 222, 128, 0.15);
}

.type-bilibili {
  color: #f472b6;
  background: rgba(244, 114, 182, 0.15);
}

.type-paypal {
  color: #003087;
  background: rgba(0, 48, 135, 0.15);
}

/* 状态标签 */
.state-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
}

.state-pending {
  color: #fbbf24;
  background: rgba(251, 191, 36, 0.15);
}

.state-success {
  color: #4ade80;
  background: rgba(74, 222, 128, 0.15);
}

.state-failed {
  color: #f87171;
  background: rgba(248, 113, 113, 0.15);
}

.state-confirming {
  color: #60a5fa;
  background: rgba(96, 165, 250, 0.15);
}

.state-closed {
  color: #94a3b8;
  background: rgba(148, 163, 184, 0.15);
}

/* 空状态 */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
}

.empty-state i {
  font-size: 3rem;
  margin-bottom: 16px;
  color: #475569;
}

.empty-state p {
  font-size: 0.95rem;
}

/* 分页 */
.pagination-wrapper {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid rgba(94, 234, 212, 0.1);
}

:deep(.el-pagination) {
  --el-pagination-bg-color: transparent !important;
  --el-pagination-button-bg-color: rgba(51, 65, 85, 0.6) !important;
  --el-pagination-hover-color: #5eead4 !important;
}

:deep(.el-pagination .el-pager li) {
  background: rgba(51, 65, 85, 0.6) !important;
  border-radius: 6px !important;
  margin: 0 2px;
  color: #cbd5e1 !important;
}

:deep(.el-pagination .el-pager li.is-active) {
  background: rgba(8, 239, 204, 0.2) !important;
  color: #5eead4 !important;
}

:deep(.el-pagination .btn-prev),
:deep(.el-pagination .btn-next) {
  background: rgba(51, 65, 85, 0.6) !important;
  border-radius: 6px !important;
  color: #cbd5e1 !important;
}

:deep(.el-pagination .el-pagination__total),
:deep(.el-pagination .el-pagination__jump) {
  color: #64748b !important;
}

:deep(.el-pagination .el-select .el-input .el-input__inner) {
  color: #5eead4 !important;
}

:deep(.el-pagination .el-input__wrapper) {
  background: rgba(51, 65, 85, 0.6) !important;
  box-shadow: none !important;
  border: 1px solid rgba(94, 234, 212, 0.15) !important;
}

@media (max-width: 768px) {
  .dialog-content {
    max-height: 90vh;
  }

  .container {
    padding: 20px 16px;
  }

  .power-order-dialog {
    padding: 10px;
  }

  .order-table {
    font-size: 0.78rem;
  }

  .order-table th,
  .order-table td {
    padding: 8px 6px;
  }
}
</style>
